// components/GroupThreeDotMenu.tsx - Add selection options
import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import RefreshIcon from "@mui/icons-material/Refresh";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import { AvatarGroup, CustomPhotoAvatar } from "../../../../context/types";

interface GroupThreeDotMenuProps {
  group: AvatarGroup;
  selectedAvatarLabActors: CustomPhotoAvatar[];
  onOpenLookCreation: (group: AvatarGroup) => void;
  onOpenChooseLook: (group: AvatarGroup) => void;
  onRefreshGroup: (groupId: string) => void;
  onSelectAllLooks?: (group: AvatarGroup) => void;
  onDeselectAllLooks?: (group: AvatarGroup) => void;
  onDeleteGroup?: (groupId: string) => void;
  // Avatar Lab: open the dialog that sets a default voice for every look in this group.
  onSetDefaultVoice?: (group: AvatarGroup) => void;
}

const GroupThreeDotMenu: React.FC<GroupThreeDotMenuProps> = ({
  group,
  selectedAvatarLabActors,
  onOpenLookCreation,
  onOpenChooseLook,
  onRefreshGroup,
  onSelectAllLooks,
  onDeselectAllLooks,
  onDeleteGroup,
  onSetDefaultVoice,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddLook = () => {
    onOpenLookCreation(group);
    handleMenuClose();
  };

  const handleChooseLook = () => {
    onOpenChooseLook(group);
    handleMenuClose();
  };

  const handleRefresh = () => {
    onRefreshGroup(group._id);
    handleMenuClose();
  };

  const handleDeleteGroup = () => {
    onDeleteGroup?.(group._id);
    handleMenuClose();
  };

  const handleSetDefaultVoice = () => {
    onSetDefaultVoice?.(group);
    handleMenuClose();
  };

  const handleSelectAll = () => {
    onSelectAllLooks?.(group);
    handleMenuClose();
  };

  const handleDeselectAll = () => {
    onDeselectAllLooks?.(group);
    handleMenuClose();
  };

  const canAddLook = group.training_status === "ready";
  const selectedLooksCount = selectedAvatarLabActors.filter(
    (look) => look.avatar_group === group._id
  ).length;
  const hasSelectedLooks = selectedLooksCount > 0;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      style={{ display: "inline-block" }}
    >
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopImmediatePropagation();
        }}
      >
        {canAddLook && (
          <MenuItem onClick={handleAddLook}>
            <ListItemIcon>
              <AddPhotoAlternateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add New Look</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleChooseLook}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Choose/manage looks</ListItemText>
        </MenuItem>
        {onSetDefaultVoice && (
          <MenuItem onClick={handleSetDefaultVoice}>
            <ListItemIcon>
              <RecordVoiceOverIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {group.default_voice_id ? "Change default voice" : "Set default voice"}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteGroup}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Group</ListItemText>
        </MenuItem>

        {/*group.looks && group.looks.length > 1 && (
          <MenuItem onClick={handleSelectAll}>
            <ListItemIcon>
              <SelectAllIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Select All Looks</ListItemText>
          </MenuItem>
        )*/}

        {/*hasSelectedLooks && (
          <MenuItem onClick={handleDeselectAll}>
            <ListItemIcon>
              <DeselectIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Deselect All Looks</ListItemText>
          </MenuItem>
        )*/}

        {(group.training_status === "training" ||
          group.training_status === "pending") && (
          <MenuItem onClick={handleRefresh}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Refresh Status</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default GroupThreeDotMenu;
